import { useEffect, useState } from "react";
import { Users, Phone, Mail, Search } from "lucide-react";
import { toast } from "sonner";
import api, { formatPrice, formatApiError } from "../../lib/api";

/**
 * Saytda hesab açmış müştərilər.
 *
 * Qeyd: müştərilərin böyük hissəsi hesab AÇMADAN alış edir (qonaq sifarişi) —
 * bu, normaldır və satışa mane olmur. Ona görə səhifədə qonaq sifarişlərinin
 * sayı da göstərilir, əks halda siyahının qısalığı yanlış təəssürat yaradır.
 */
const AdminCustomers = () => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/admin/users")
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)));
  }, []);

  if (!data) {
    return <p className="text-brand-muted py-10 text-center">Yüklənir...</p>;
  }

  const needle = q.trim().toLowerCase();
  const list = needle
    ? data.users.filter((u) =>
        [u.name, u.email, u.phone].some((v) => (v || "").toLowerCase().includes(needle)))
    : data.users;

  const day = (s) => (s ? new Date(s).toLocaleDateString() : "—");

  return (
    <div data-testid="admin-customers">
      <h1 className="font-serif font-light text-3xl sm:text-4xl tracking-tight mb-2">
        Müştərilər
      </h1>
      <p className="text-xs text-brand-muted mb-6 leading-relaxed">
        Saytda hesab açmış <b>{data.users.length}</b> müştəri.
        Bundan əlavə <b>{data.guest_orders}</b> sifariş hesab açmadan (qonaq kimi) verilib —
        bu normaldır, hesab açmaq məcburi deyil.
      </p>

      {data.users.length > 3 && (
        <div className="relative mb-5">
          <Search size={16} strokeWidth={1.5}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Ad, email və ya nömrə ilə axtar"
            data-testid="customer-search"
            className="w-full border border-brand-line bg-white rounded-md pl-9 pr-3 py-2.5 text-sm outline-none focus:border-brand-ink"
          />
        </div>
      )}

      {list.length === 0 ? (
        <p className="text-brand-muted py-10 text-center">Müştəri yoxdur</p>
      ) : (
        <div className="space-y-3">
          {list.map((u) => (
            <div key={u.id}
                 data-testid={`customer-${u.id}`}
                 className="bg-white border border-brand-line rounded-md p-4 sm:p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium flex items-center gap-2">
                    <Users size={15} strokeWidth={1.5} className="text-brand-muted shrink-0" />
                    {u.name || "—"}
                    {u.discount_percent > 0 && (
                      <span className="text-xs text-brand-oxid border border-brand-line px-2 py-0.5 rounded">
                        endirim {u.discount_percent}%
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-brand-muted mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1">
                    {u.phone && (
                      <a href={`tel:${u.phone}`} className="flex items-center gap-1 hover:text-brand-ink">
                        <Phone size={12} strokeWidth={1.5} /> {u.phone}
                      </a>
                    )}
                    {u.email && (
                      <a href={`mailto:${u.email}`} className="flex items-center gap-1 hover:text-brand-ink">
                        <Mail size={12} strokeWidth={1.5} /> {u.email}
                      </a>
                    )}
                  </p>
                </div>

                <div className="text-right shrink-0">
                  <p className="font-serif text-xl">{formatPrice(u.spent)}</p>
                  <p className="text-xs text-brand-muted mt-0.5">
                    {u.paid_orders} ödənilmiş sifariş
                  </p>
                </div>
              </div>

              <div className="mt-3 pt-3 border-t border-brand-line text-xs text-brand-muted flex flex-wrap gap-x-6 gap-y-1">
                <span>Qeydiyyat: {day(u.created_at)}</span>
                <span>Son sifariş: {day(u.last_order)}</span>
                {u.orders > u.paid_orders && (
                  <span>Yarımçıq qalan: {u.orders - u.paid_orders}</span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminCustomers;
